/**
 * This is the entry point for Feature Hub App integration
 */

import React from 'react';
import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import type { Logger } from '@feature-hub/logger';
import type { ReactFeatureApp } from '@feature-hub/react';
import { FootnoteContextProvider } from '@oneaudi/feature-app-utils';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import App from './FeatureApp';
import { ContextProvider } from './Context';
import type {
  ContentService,
  AudiFootnoteReferenceServiceScopeManager,
  GfaLocaleService,
  I18NService,
  LocaleService,
  LayerManager,
  RenderModeService,
  TrackingService,
  VueFormatterServiceInterface,
} from './feature-service-types';
import { getDesignLibrary } from './utils/alpha-helper';
import type { EditorialColumnsProps } from '../types';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'gfa:locale-service': GfaLocaleService;
  readonly 'audi-content-service': ContentService;
  readonly 'locale-service': LocaleService;
  readonly 'dbad:audi-vue-formatter-service': VueFormatterServiceInterface;
  readonly 'audi-tracking-service'?: TrackingService;
  readonly 'audi-footnote-reference-service': AudiFootnoteReferenceServiceScopeManager;
  readonly 'dbad:audi-i18n-service': I18NService;
  readonly 'layer-manager': LayerManager;
  readonly 'audi-render-mode-service'?: RenderModeService;
}

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      'audi-content-service': '^1.0.0',
      'locale-service': '^1.0.0',
    },
    externals: {
      react: '*',
      'styled-components': '^5',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.14.0' }),
    },
  },
  optionalDependencies: {
    featureServices: {
      'dbad:audi-vue-formatter-service': '^1.0.0',
      'audi-tracking-service': '^2.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      'layer-manager': '^2.7.2',
      'audi-render-mode-service': '^1.0.0',
    },
  },

  create: ({
    featureServices,
    featureAppId,
    featureAppName,
    config,
  }: FeatureAppEnvironment<FeatureServiceDependencies, EditorialColumnsProps | undefined>) => {
    const loggerService = featureServices['s2:logger'];
    loggerService?.info('Feature App created.');

    const contentService = featureServices['audi-content-service'];

    const trackingService = featureServices['audi-tracking-service'] as TrackingService;
    if (trackingService) {
      trackingService.featureAppName = __FEATURE_APP_NAME__;
    }

    const vueFormatterService = featureServices['dbad:audi-vue-formatter-service'];
    const localeService = featureServices['locale-service'];
    const renderModeService = featureServices['audi-render-mode-service'];
    const footnoteReferenceServiceScopeManager = featureServices['audi-footnote-reference-service'];
    const footnoteReferenceService =
      footnoteReferenceServiceScopeManager?.getDefaultScopeRefService();
    const layerManager = featureServices['layer-manager'];

    return {
      render: () => {
        return (
          <FootnoteContextProvider footnoteReferenceService={footnoteReferenceService}>
            <div data-fefa-custom-id={footnoteReferenceService.getConsumerId()}>
              <ContextProvider
                featureAppEnv={{ featureAppId, featureAppName }}
                localeService={localeService}
                logger={loggerService}
                trackingService={trackingService}
                vueFormatterService={vueFormatterService}
                layerManager={layerManager}
              >
                <UniversalEditorProvider
                  contentService={contentService}
                  renderModeService={renderModeService}
                >
                  <App contentService={contentService} initialContent={config} />
                </UniversalEditorProvider>
              </ContextProvider>
            </div>
          </FootnoteContextProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
